import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Breadcrumbs, Button, IconButton, Skeleton, Typography } from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import AppsContainer from '@tenant/core/AppsContainer';
import AppsHeader from '@tenant/core/AppsContainer/AppsHeader';
import useGoBack from '@tenant/utility/hooks/useGoBack';
import { ROUTE_STRING } from 'constants/RouteString';
import useGetListPipelineRuns from 'redux/slices/pipelines/hooks/useGetListPipelineRuns';
import { resolveRunBranchDisplay } from 'redux/slices/pipelines/helpers/resolveRunBranchDisplay';
import PipelineRunStatusChip from './components/Common/PipelineRunStatusChip';

// AC-17 — run history for one pipeline: GET /api/pipelines/{id}/runs, rendered newest-first
// (the API already returns them newest-first; we do not re-sort and risk fighting it). Each
// row deep-links to the single-run live status view (RUN_VIEW). Gated PIPELINE_READ in
// ./index.js. Also the landing view for the VIEW route (/pipelines/:id).
const formatDate = (value) => (value ? new Date(value).toLocaleString() : '-');

const RunHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { data, isLoading, isError, refetch } = useGetListPipelineRuns(id);
  const runs = data?.data ?? [];

  const columns = [
    {
      field: 'Status',
      headerName: 'Status',
      width: 160,
      sortable: false,
      renderCell: (params) => <PipelineRunStatusChip status={params.value} />,
    },
    {
      // deploy-pipelines-run-overrides (Wave 4, AC-10) — this run's OWN branch, so an override
      // run stays distinguishable from a default run after the fact, on the page whose whole
      // job is reading run history.
      field: 'BranchName',
      headerName: 'Branch',
      flex: 1,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => resolveRunBranchDisplay(params.value),
    },
    {
      field: 'StartedAt',
      headerName: 'Started',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'CompletedAt',
      headerName: 'Completed',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'Action',
      headerName: '',
      width: 50,
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem
          key='run-action--view'
          label='View'
          showInMenu
          onClick={() =>
            navigate(
              ROUTE_STRING.PIPELINE.RUN_VIEW.replace(':id', id).replace(':runId', params.row.Id),
            )
          }
        />,
      ],
    },
  ];

  return (
    <AppsContainer
      fullView
      title={
        <Breadcrumbs aria-label='breadcrumb'>
          <Typography color='text.primary'>Deployment</Typography>
          <Typography color='text.primary'>
            <Link to={ROUTE_STRING.PIPELINE.LIST}>Pipelines</Link>
          </Typography>
          <Typography color='text.primary'>Runs</Typography>
        </Breadcrumbs>
      }
    >
      <AppsHeader>
        <Box display='flex' alignItems='center' gap={4} sx={{ width: '100%' }}>
          {/* The run history's parent is the PIPELINE, not the pipelines list — the only
              thing that links here is the detail page's RunConsole ("View all runs"), so
              sending back to the list skipped a level even on the one path that exists. */}
          <ArrowBackIcon
            sx={{ color: 'text.secondary', cursor: 'pointer' }}
            onClick={() => goBack(ROUTE_STRING.PIPELINE.VIEW.replace(':id', id))}
          />
          <Typography variant='h4'>
            Run history
            <IconButton title='Refresh' size='small' onClick={() => refetch()}>
              <RefreshIcon fontSize='inherit' />
            </IconButton>
          </Typography>
        </Box>
      </AppsHeader>

      <Box p={4}>
        {isLoading && <Skeleton variant='rounded' height={160} />}

        {!isLoading && isError && (
          <Alert
            severity='warning'
            action={
              <Button color='inherit' size='small' onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Could not load run history. Please try again.
          </Alert>
        )}

        {!isLoading && !isError && runs.length === 0 && (
          <Alert severity='info'>This pipeline has no runs yet.</Alert>
        )}

        {!isLoading && !isError && runs.length > 0 && (
          <Box sx={{ width: '100%' }}>
            <DataGrid
              hideFooter
              autoHeight
              disableColumnSelector
              disableColumnMenu
              rows={runs}
              columns={columns}
              getRowId={(row) => row.Id}
            />
          </Box>
        )}
      </Box>
    </AppsContainer>
  );
};

export default React.memo(RunHistory);
